import React, { useEffect, useState } from 'react';
import { Box, Grid2, Stack, Typography } from '@mui/material';
import ShowContainer from '../../../components/material-ui/ShowContainer';
import CusTooltip from '../../../components/material-ui/CusTooltip';
import CurrencySelect from '../../../components/shared/CurrencySelect';
import TypographyText from '../../../components/material-ui/table/TypographyText';
import { API_USERS } from '../../../api/users';
import { useTranslation } from 'react-i18next';
import CusSiteDateTime from '../../../components/material-ui/CusSiteDateTime';
import LoaderContainer from '../../../components/material-ui/loader_container/LoaderContainer';
import useBootstrapSize from '../../../hooks/useBootstrapSize';

export default function FinanceProfileContent({ userId, defaultCurrency, setTitleNick }) {
  const { t } = useTranslation();
  const { isDesktop } = useBootstrapSize();

  const currencyRow = { title: 'Currency', value: defaultCurrency, isSelect: true };
  const grigSize = { xs: 12, lg: 6 };

  const [serverData, setServerData] = useState({});
  const [fields, setFields] = useState([currencyRow]);
  const [currency, setCurrency] = useState(fields[0]?.value);
  const [loader, setLoader] = useState(false);

  const selectedCurrency = fields[1]?.value;

  const getFieldsStruct = (obj, currency) => [
    {
      title: 'Registration date',
      value: <CusSiteDateTime date={obj.user?.register} />,
      order: 1
    },
    { ...currencyRow, value: currency, order: 2 },

    {
      mt: 3,
      title: 'Main balance',
      value: obj.balance?.main + obj.balance?.deposit,
      tooltip: "The main balance is 'open for payment' plus unplayed deposits",
      isCurrency: true,
      order: 3
    },
    {
      title: 'Bonus balance',
      value: obj.balance?.bonus,
      tooltip:
        'The bonus balance consists of previously received bonuses, which can only be used in bonus games',
      isCurrency: true,
      order: isDesktop ? 5 : 4
    },
    {
      title: 'Amount of deposits',
      value: obj.deposit?.totalAmount,
      tooltip: 'The sum of all successful player deposits since account registration.',
      isCurrency: true,
      order: isDesktop ? 7 : 5
    },
    {
      title: 'Deposits, total',
      value: obj.deposit?.totalCount ?? '-',
      thing: true,
      order: isDesktop ? 9 : 6
    },
    {
      title: 'Deposits in the last 7 days',
      value: obj.deposit?.count7Days ?? '-',
      thing: true,
      order: isDesktop ? 11 : 7
    },
    {
      mt: isDesktop ? 3 : 0,
      title: 'Amount of withdrawals',
      value: obj.payout?.totalAmount,
      tooltip: 'The sum of all successful payouts to the player since the account was registered',
      isCurrency: true,
      order: isDesktop ? 4 : 8
    },
    {
      title: 'Conclusions, total',
      value: obj.payout?.totalCount ?? '-',
      thing: true,
      order: isDesktop ? 6 : 9
    },
    {
      title: 'Income',
      value: obj.profit,
      tooltip: 'Income = Deposits - Withdrawals',
      isCurrency: true,
      order: isDesktop ? 13 : 10
    },
    {
      title: 'Gross Gaming Revenue (GGR)',
      value: obj.GGR,
      tooltip:
        'The difference between the total bet in a game and the total winnings. GGR = total bet in a game - total winnings',
      isCurrency: true,
      order: isDesktop ? 8 : 11
    },
    {
      title: 'Gross Gaming Revenue Margin (GGRM)',
      value: obj.GGRM ? obj.GGRM + '%' : '-',
      tooltip:
        'Gross Gambling Margin (GGR) is calculated as the gross gambling revenue as a percentage of the total bets. GGRM = (GGR / total bets in the game) * 100%',
      order: isDesktop ? 10 : 12
    },
    {
      title: 'Net Return on Player (NGR)',
      value: obj.NGR,
      isCurrency: true,
      tooltip:
        'NGR = Total deposits - Gateway deposit fee - Players withdrawal amount - Gateway withdrawal fee - Game rental cost',
      order: isDesktop ? 12 : 13
    },
    {
      title: 'Return to Player (RTP)',
      value: obj.RTP ? obj.RTP + '%' : '-',
      tooltip: 'Average return to player (winnings) % across all games by currency',
      order: 14
    }
  ];

  useEffect(() => {
    if (!currency) return;
    if (!userId) return;

    setLoader?.(true);
    API_USERS.finance({ userId: +userId, currency })
      .then((e) => {
        const data = e.data.data || {};
        if (!Object.keys(data).length) {
          setFields([currencyRow]);
          return;
        }

        setServerData(data);
        setTitleNick?.(data.user?.login);
      })
      .catch(() => setFields([currencyRow]))
      .finally(() => {
        setLoader?.(false);
      });
  }, [userId, currency]);

  useEffect(() => {
    if (!Object.keys(serverData).length) return;

    setFields(getFieldsStruct(serverData, currency));
  }, [isDesktop, serverData]);

  return (
    <LoaderContainer
      loader={loader}
      boxSx={{ maxHeight: 'calc(100svh - 250px)', overflow: 'hidden' }}>
      <Grid2 container columnSpacing={5} rowSpacing={0}>
        {fields.map((info, indx) => {
          return (
            <Grid2 size={grigSize} key={indx} mt={info.mt} order={info.order}>
              <Stack
                direction={'row'}
                justifyContent={'space-between'}
                p={1}
                sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}>
                <Stack direction={'row'} alignItems={'center'} gap={1}>
                  <Typography color="textSecondary" fontSize={12} component={'span'}>
                    {t(info.title)}
                  </Typography>
                  <ShowContainer condition={info.tooltip}>
                    <CusTooltip text={info.tooltip} />
                  </ShowContainer>
                </Stack>
                {info.isSelect ? (
                  <Box sx={{ height: '21px' }}>
                    <CurrencySelect value={currency} setValue={setCurrency} hideLabel isVerySmall />
                  </Box>
                ) : (
                  <TypographyText fontSize={14} fontWeight={600}>
                    {info.value}{' '}
                    <TypographyText color="textSecondary">
                      {info.isCurrency ? selectedCurrency : undefined}
                      {info.thing ? t('pcs.') : undefined}
                    </TypographyText>
                  </TypographyText>
                )}
              </Stack>
            </Grid2>
          );
        })}
        <ShowContainer condition={fields.length <= 1}>
          <Box
            width={'100%'}
            mt={2}
            sx={{
              background: (theme) => theme.palette.grey[200],
              color: (th) => th.palette.text.secondary,
              borderRadius: 1,
              p: 2
            }}>
            <Typography fontSize={16} fontWeight={600}>
              {currency ? t('Please change the currency') : t('Please select currency')}
            </Typography>
          </Box>
        </ShowContainer>
      </Grid2>
    </LoaderContainer>
  );
}
